import { useCallback, useEffect, useState } from "react";

import {
  createAdminAction,
  createAdminEvent,
  createAdminMarketItem,
  createAdminMarketSection,
  createAdminMenuCategory,
  createAdminMenuItem,
  deleteAdminAction,
  deleteAdminEvent,
  deleteAdminMarketItem,
  deleteAdminMarketSection,
  deleteAdminMenuCategory,
  deleteAdminMenuItem,
  getAdminActions,
  getAdminActionEntries,
  getAdminEvents,
  getAdminGuestBase,
  getAdminHostessUsers,
  getAdminMarketItems,
  getAdminMarketReservations,
  getAdminMarketSections,
  getAdminMenuCategories,
  getAdminMenuItems,
  getAdminTournament,
  patchAdminAction,
  patchAdminEvent,
  patchAdminMarketItem,
  patchAdminMarketSection,
  patchAdminMenuCategory,
  patchAdminMenuItem,
  postAdminTournamentMatchResult,
  seedAdminTournament,
  uploadAdminEventImage,
  uploadAdminMarketImage,
} from "../api/api";
import { Button } from "../components/Button";
import { AdminActionsSection } from "./admin/AdminActionsSection";
import { AdminGuestBaseSection } from "./admin/AdminGuestBaseSection";
import { AdminEventsSection } from "./admin/AdminEventsSection";
import { AdminMarketSection } from "./admin/AdminMarketSection";
import { AdminMenuSection } from "./admin/AdminMenuSection";

export function Admin({ onOpenGame = () => {}, canManageHostess = false }) {
  const [section, setSection] = useState("events");
  const [actions, setActions] = useState([]);
  const [actionEntriesById, setActionEntriesById] = useState({});
  const [loadingActionEntriesId, setLoadingActionEntriesId] = useState(null);
  const [editingActionId, setEditingActionId] = useState(null);
  const [events, setEvents] = useState([]);
  const [marketItems, setMarketItems] = useState([]);
  const [marketReservations, setMarketReservations] = useState([]);
  const [marketSections, setMarketSections] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [guestBaseRows, setGuestBaseRows] = useState([]);
  const [guestBaseSearch, setGuestBaseSearch] = useState("");
  const [guestBaseLoading, setGuestBaseLoading] = useState(false);
  const [hostessRows, setHostessRows] = useState([]);
  const [hostessSearch, setHostessSearch] = useState("");
  const [hostessLoading, setHostessLoading] = useState(false);
  const [tournamentGuestSearchByEventId, setTournamentGuestSearchByEventId] = useState({});
  const [tournamentGuestCandidatesByEventId, setTournamentGuestCandidatesByEventId] =
    useState({});
  const [loadingTournamentGuestSearchEventId, setLoadingTournamentGuestSearchEventId] =
    useState(null);
  const [savingTournamentParticipantsEventId, setSavingTournamentParticipantsEventId] =
    useState(null);
  const [openedTournamentParticipantsEventId, setOpenedTournamentParticipantsEventId] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadingEventImage, setUploadingEventImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [actionDraft, setActionDraft] = useState({
    kind: "action",
    title: "",
    description: "",
    notify_on_activate: false,
    raffle_seats: "3",
    raffle_prizes: [
      { title: "", is_bonus_points: false, bonus_amount: "" },
      { title: "", is_bonus_points: false, bonus_amount: "" },
      { title: "", is_bonus_points: false, bonus_amount: "" },
    ],
    raffle_draw_at: "",
    is_active: false,
  });
  const [eventDraft, setEventDraft] = useState({
    kind: "event",
    title: "",
    date: "",
    time: "",
    description: "",
    image_url: "",
    is_published: false,
  });
  const [marketDraft, setMarketDraft] = useState({
    section_id: "",
    title: "",
    description: "",
    price: "",
    price_bonus: "",
    stock_quantity: "",
    image_url: "",
    image_urls: [],
    is_active: false,
  });
  const [editingMarketItemId, setEditingMarketItemId] = useState(null);
  const [sectionDraft, setSectionDraft] = useState({
    title: "",
    sort_order: 0,
    is_active: true,
  });
  const [categoryDraft, setCategoryDraft] = useState({
    parent_id: "",
    title: "",
    sort_order: 0,
  });
  const [itemDraft, setItemDraft] = useState({
    category_id: "",
    title: "",
    description: "",
    price_rub: "",
    sort_order: "0",
  });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingMenuItemId, setEditingMenuItemId] = useState(null);

  const loadSection = useCallback(
    async (targetSection) => {
      if (targetSection === "guestBase") {
        return;
      }
      if (targetSection === "hostess") {
        return;
      }
      setLoading(true);
      setError("");
      try {
        if (targetSection === "events") {
          setEvents(await getAdminEvents());
        } else if (targetSection === "actions") {
          setActions(await getAdminActions());
        } else if (targetSection === "market") {
          const [sectionsData, itemsData, reservationsData] = await Promise.all([
            getAdminMarketSections(),
            getAdminMarketItems(),
            getAdminMarketReservations(),
          ]);
          setMarketSections(sectionsData);
          setMarketItems(itemsData);
          setMarketReservations(reservationsData);
          setMarketDraft((prev) => ({
            ...prev,
            section_id:
              prev.section_id || (sectionsData[0] ? String(sectionsData[0].id) : ""),
          }));
        } else {
          const [categoriesData, itemsData] = await Promise.all([
            getAdminMenuCategories(),
            getAdminMenuItems(),
          ]);
          setMenuCategories(categoriesData);
          setMenuItems(itemsData);
          setItemDraft((prev) => ({
            ...prev,
            category_id: prev.category_id || (categoriesData[0] ? String(categoriesData[0].id) : ""),
          }));
        }
      } catch (err) {
        setError(err?.message || "Не удалось загрузить данные админки");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSection(section);
  }, [section, loadSection]);

  const loadGuestBase = useCallback(async (q) => {
    setGuestBaseLoading(true);
    setError("");
    try {
      setGuestBaseRows(await getAdminGuestBase({ q }));
    } catch (err) {
      setError(err?.message || "Не удалось загрузить базу гостей");
      setGuestBaseRows([]);
    } finally {
      setGuestBaseLoading(false);
    }
  }, []);

  const loadHostess = useCallback(async (q) => {
    setHostessLoading(true);
    setError("");
    try {
      setHostessRows(await getAdminHostessUsers({ q }));
    } catch (err) {
      setError(err?.message || "Не удалось загрузить список хостес");
      setHostessRows([]);
    } finally {
      setHostessLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section !== "guestBase") return;
    loadGuestBase(guestBaseSearch);
  }, [section, guestBaseSearch, loadGuestBase]);

  useEffect(() => {
    if (section !== "hostess") return;
    loadHostess(hostessSearch);
  }, [section, hostessSearch, loadHostess]);

  const onSectionChange = (event) => {
    setSection(event.target.value);
  };

  useEffect(() => {
    if (section === "hostess" && !canManageHostess) {
      setSection("guestBase");
    }
  }, [section, canManageHostess]);

  const confirmDelete = (entityLabel, itemTitle) => {
    const title = String(itemTitle || "").trim();
    const target = title ? `«${title}»` : "эту запись";
    return window.confirm(`Удалить ${entityLabel} ${target}?`);
  };

  // ---- events ----
  const onEventSubmit = async (event) => {
    event.preventDefault();
    if (
      !eventDraft.title.trim() ||
      !eventDraft.date.trim() ||
      !eventDraft.time.trim() ||
      !eventDraft.description.trim()
    )
      return;
    try {
      await createAdminEvent(eventDraft);
      setEventDraft({
        kind: "event",
        title: "",
        date: "",
        time: "",
        description: "",
        image_url: "",
        is_published: false,
      });
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось создать мероприятие");
    }
  };

  const onEventImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingEventImage(true);
    setError("");
    try {
      const data = await uploadAdminEventImage(file);
      if (data?.url) setEventDraft((prev) => ({ ...prev, image_url: data.url }));
    } catch (err) {
      setError(err?.message || "Не удалось загрузить изображение мероприятия");
    } finally {
      setUploadingEventImage(false);
      event.target.value = "";
    }
  };

  const togglePublished = async (item) => {
    try {
      await patchAdminEvent(item.id, { is_published: !item.is_published });
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось изменить публикацию");
    }
  };

  const removeEvent = async (item) => {
    if (!confirmDelete("мероприятие", item?.title)) return;
    try {
      await deleteAdminEvent(item.id);
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось удалить мероприятие");
    }
  };

  const searchTournamentGuests = useCallback(async (eventId, q) => {
    setLoadingTournamentGuestSearchEventId(eventId);
    try {
      const rows = await getAdminGuestBase({ q, limit: 100 });
      setTournamentGuestCandidatesByEventId((prev) => ({
        ...prev,
        [eventId]: Array.isArray(rows) ? rows : [],
      }));
    } catch (err) {
      setError(err?.message || "Не удалось загрузить базу гостей");
      setTournamentGuestCandidatesByEventId((prev) => ({
        ...prev,
        [eventId]: [],
      }));
    } finally {
      setLoadingTournamentGuestSearchEventId((prev) => (prev === eventId ? null : prev));
    }
  }, []);

  const onTournamentGuestSearchChange = (eventId, q) => {
    setTournamentGuestSearchByEventId((prev) => ({ ...prev, [eventId]: q }));
    void searchTournamentGuests(eventId, q);
  };

  const toggleTournamentParticipantsOpen = (item) => {
    if (item.kind !== "tournament") return;
    setOpenedTournamentParticipantsEventId((prev) => {
      const next = prev === item.id ? null : item.id;
      if (next === item.id && !tournamentGuestCandidatesByEventId[item.id]) {
        const q = tournamentGuestSearchByEventId[item.id] || "";
        void searchTournamentGuests(item.id, q);
      }
      return next;
    });
  };

  const addTournamentParticipant = async (item, row) => {
    if (item.kind !== "tournament") return;
    const existing = Array.isArray(item.tournament_participants)
      ? item.tournament_participants
      : [];
    if (existing.some((x) => Number(x?.user_id) === Number(row.id))) return;
    const fullName = [row.profile_last_name, row.profile_first_name, row.profile_patronymic]
      .filter(Boolean)
      .join(" ")
      .trim();
    const next = [
      ...existing,
      {
        user_id: Number(row.id),
        telegram_id: Number(row.telegram_id),
        full_name: fullName || row.profile_nickname || row.username || `Гость ${row.telegram_id}`,
        username: row.username || null,
        phone: row.profile_phone || null,
      },
    ];
    setSavingTournamentParticipantsEventId(item.id);
    try {
      await patchAdminEvent(item.id, { tournament_participants: next });
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось добавить участника турнира");
    } finally {
      setSavingTournamentParticipantsEventId((prev) => (prev === item.id ? null : prev));
    }
  };

  const removeTournamentParticipant = async (item, userId) => {
    if (item.kind !== "tournament") return;
    const existing = Array.isArray(item.tournament_participants)
      ? item.tournament_participants
      : [];
    const next = existing.filter((x) => Number(x?.user_id) !== Number(userId));
    setSavingTournamentParticipantsEventId(item.id);
    try {
      await patchAdminEvent(item.id, { tournament_participants: next });
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось удалить участника турнира");
    } finally {
      setSavingTournamentParticipantsEventId((prev) => (prev === item.id ? null : prev));
    }
  };

  const seedTournament = async (item) => {
    try {
      await seedAdminTournament(item.id);
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось распределить участников");
    }
  };

  const pickTournamentWinner = async (item, matchId, winnerUserId) => {
    try {
      await postAdminTournamentMatchResult(item.id, matchId, winnerUserId);
      await loadSection("events");
    } catch (err) {
      setError(err?.message || "Не удалось сохранить результат матча");
    }
  };

  const refreshTournament = async (item) => {
    try {
      const bracket = await getAdminTournament(item.id);
      setEvents((prev) =>
        prev.map((x) =>
          x.id === item.id ? { ...x, tournament_bracket: bracket || null } : x,
        ),
      );
    } catch (err) {
      setError(err?.message || "Не удалось обновить сетку турнира");
    }
  };

  // ---- actions ----
  const onActionSubmit = async (event) => {
    event.preventDefault();
    if (!actionDraft.title.trim() || !actionDraft.description.trim()) return;
    try {
      const payload =
        actionDraft.kind === "raffle"
          ? {
              kind: "raffle",
              title: actionDraft.title,
              description: actionDraft.description,
              raffle_seats: Number(actionDraft.raffle_seats || 0),
              raffle_prizes: actionDraft.raffle_prizes.map((x) => ({
                title: String(x?.title || "").trim(),
                is_bonus_points: Boolean(x?.is_bonus_points),
                bonus_amount: x?.is_bonus_points ? Number(x?.bonus_amount || 0) : null,
              })),
              raffle_draw_at: actionDraft.raffle_draw_at,
              notify_on_activate: actionDraft.notify_on_activate,
              is_active: actionDraft.is_active,
            }
          : {
              kind: "action",
              title: actionDraft.title,
              description: actionDraft.description,
              notify_on_activate: actionDraft.notify_on_activate,
              is_active: actionDraft.is_active,
            };
      if (editingActionId) {
        await patchAdminAction(editingActionId, payload);
      } else {
        await createAdminAction(payload);
      }
      setActionDraft({
        kind: "action",
        title: "",
        description: "",
        notify_on_activate: false,
        raffle_seats: "3",
        raffle_prizes: [
          { title: "", is_bonus_points: false, bonus_amount: "" },
          { title: "", is_bonus_points: false, bonus_amount: "" },
          { title: "", is_bonus_points: false, bonus_amount: "" },
        ],
        raffle_draw_at: "",
        is_active: false,
      });
      setEditingActionId(null);
      await loadSection("actions");
    } catch (err) {
      setError(err?.message || "Не удалось сохранить акцию");
    }
  };

  const startEditAction = (item) => {
    const isRaffle = item.kind === "raffle";
    const seats = Math.max(1, Number(item.raffle_seats || 1));
    const sourcePrizes = Array.isArray(item.raffle_prizes) ? item.raffle_prizes : [];
    const normalizedPrizes = Array.from({ length: seats }, (_, i) => {
      const p = sourcePrizes[i] || {};
      return {
        title: String(p?.title || ""),
        is_bonus_points: Boolean(p?.is_bonus_points),
        bonus_amount: p?.is_bonus_points ? String(p?.bonus_amount || "") : "",
      };
    });
    setEditingActionId(item.id);
    setActionDraft({
      kind: isRaffle ? "raffle" : "action",
      title: item.title || "",
      description: item.description || "",
      notify_on_activate: false,
      raffle_seats: String(seats),
      raffle_prizes: normalizedPrizes,
      raffle_draw_at: isRaffle ? (item.raffle_draw_at || "") : "",
      is_active: Boolean(item.is_active),
    });
  };

  const cancelEditAction = () => {
    setEditingActionId(null);
    setActionDraft({
      kind: "action",
      title: "",
      description: "",
      notify_on_activate: false,
      raffle_seats: "3",
      raffle_prizes: [
        { title: "", is_bonus_points: false, bonus_amount: "" },
        { title: "", is_bonus_points: false, bonus_amount: "" },
        { title: "", is_bonus_points: false, bonus_amount: "" },
      ],
      raffle_draw_at: "",
      is_active: false,
    });
  };

  const toggleAction = async (item) => {
    try {
      const nextActive = !item.is_active;
      let notify_on_activate = false;
      if (nextActive) {
        notify_on_activate = window.confirm(
          "Оповестить всех пользователей о запуске этой акции?",
        );
      }
      await patchAdminAction(item.id, { is_active: nextActive, notify_on_activate });
      await loadSection("actions");
    } catch (err) {
      setError(err?.message || "Не удалось изменить статус акции");
    }
  };

  const removeAction = async (item) => {
    if (!confirmDelete("акцию", item?.title)) return;
    try {
      await deleteAdminAction(item.id);
      await loadSection("actions");
    } catch (err) {
      setError(err?.message || "Не удалось удалить акцию");
    }
  };

  const loadActionEntries = async (actionId) => {
    setLoadingActionEntriesId(actionId);
    try {
      const rows = await getAdminActionEntries(actionId);
      setActionEntriesById((prev) => ({ ...prev, [actionId]: Array.isArray(rows) ? rows : [] }));
    } catch (err) {
      setError(err?.message || "Не удалось загрузить участников розыгрыша");
    } finally {
      setLoadingActionEntriesId(null);
    }
  };

  // ---- market ----
  const resetMarketDraft = () => {
    setMarketDraft((prev) => ({
      ...prev,
      title: "",
      description: "",
      price: "",
      price_bonus: "",
      stock_quantity: "",
      image_url: "",
      image_urls: [],
      is_active: false,
    }));
    setEditingMarketItemId(null);
  };

  const startEditMarketItem = (item) => {
    const imageUrls = Array.isArray(item.image_urls)
      ? item.image_urls.filter(Boolean)
      : item.image_url
        ? [item.image_url]
        : [];
    setEditingMarketItemId(item.id);
    setMarketDraft({
      section_id: item.section_id ? String(item.section_id) : "",
      title: item.title || "",
      description: item.description || "",
      price: String(item.price ?? ""),
      price_bonus: String(item.price_bonus ?? ""),
      stock_quantity: String(item.stock_quantity ?? ""),
      image_url: item.image_url || (imageUrls[0] || ""),
      image_urls: imageUrls,
      is_active: !!item.is_active,
    });
  };

  const onSetMarketPreview = (url) => {
    setMarketDraft((prev) => ({ ...prev, image_url: url }));
  };

  const onRemoveMarketImage = (url) => {
    setMarketDraft((prev) => {
      const nextUrls = prev.image_urls.filter((x) => x !== url);
      const nextPreview =
        prev.image_url === url ? (nextUrls[0] || "") : prev.image_url;
      return {
        ...prev,
        image_urls: nextUrls,
        image_url: nextPreview,
      };
    });
  };

  const onMarketSubmit = async (event) => {
    event.preventDefault();
    if (!marketDraft.title.trim() || !marketDraft.description.trim() || !marketDraft.section_id) return;
    try {
      const payload = {
        section_id: Number(marketDraft.section_id),
        title: marketDraft.title,
        description: marketDraft.description,
        price: Number(marketDraft.price || 0),
        price_bonus: Number(marketDraft.price_bonus || 0),
        stock_quantity: Number(marketDraft.stock_quantity || 0),
        image_url: marketDraft.image_url || null,
        image_urls: marketDraft.image_urls,
        is_active: marketDraft.is_active,
      };
      if (editingMarketItemId) {
        await patchAdminMarketItem(editingMarketItemId, payload);
      } else {
        await createAdminMarketItem(payload);
      }
      resetMarketDraft();
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось сохранить товар");
    }
  };

  const onMarketImageChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setUploadingImage(true);
    setError("");
    try {
      const uploaded = [];
      for (const file of files) {
        const data = await uploadAdminMarketImage(file);
        if (data?.url) uploaded.push(data.url);
      }
      if (uploaded.length > 0) {
        setMarketDraft((prev) => {
          const nextUrls = [...prev.image_urls];
          for (const url of uploaded) {
            if (!nextUrls.includes(url)) nextUrls.push(url);
          }
          return {
            ...prev,
            image_urls: nextUrls,
            image_url: prev.image_url || nextUrls[0] || "",
          };
        });
      }
    } catch (err) {
      setError(err?.message || "Не удалось загрузить изображения");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const toggleMarketItem = async (item) => {
    try {
      await patchAdminMarketItem(item.id, { is_active: !item.is_active });
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось изменить статус товара");
    }
  };

  const removeMarketItem = async (item) => {
    if (!confirmDelete("товар", item?.title)) return;
    try {
      await deleteAdminMarketItem(item.id);
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось удалить товар");
    }
  };

  const onSectionSubmit = async (event) => {
    event.preventDefault();
    if (!sectionDraft.title.trim()) return;
    try {
      await createAdminMarketSection({
        title: sectionDraft.title,
        sort_order: Number(sectionDraft.sort_order || 0),
        is_active: sectionDraft.is_active,
      });
      setSectionDraft({ title: "", sort_order: 0, is_active: true });
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось создать подраздел");
    }
  };

  const toggleMarketSection = async (section) => {
    try {
      await patchAdminMarketSection(section.id, { is_active: !section.is_active });
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось обновить подраздел");
    }
  };

  const removeMarketSection = async (section) => {
    if (!confirmDelete("подраздел маркета", section?.title)) return;
    try {
      await deleteAdminMarketSection(section.id);
      await loadSection("market");
    } catch (err) {
      setError(err?.message || "Не удалось удалить подраздел");
    }
  };

  // ---- menu ----
  const onCategorySubmit = async (event) => {
    event.preventDefault();
    if (!categoryDraft.title.trim()) return;
    try {
      const payload = {
        parent_id: categoryDraft.parent_id ? Number(categoryDraft.parent_id) : null,
        title: categoryDraft.title,
        sort_order: Number(categoryDraft.sort_order || 0),
      };
      if (editingCategoryId) {
        await patchAdminMenuCategory(editingCategoryId, payload);
      } else {
        await createAdminMenuCategory(payload);
      }
      setCategoryDraft({ parent_id: "", title: "", sort_order: 0 });
      setEditingCategoryId(null);
      await loadSection("menu");
    } catch (err) {
      setError(err?.message || "Не удалось создать уровень меню");
    }
  };

  const removeMenuCategory = async (cat) => {
    if (!confirmDelete("уровень меню", cat?.title)) return;
    try {
      await deleteAdminMenuCategory(cat.id);
      await loadSection("menu");
    } catch (err) {
      setError(err?.message || "Не удалось удалить уровень меню");
    }
  };

  const startEditCategory = (cat) => {
    setEditingCategoryId(cat.id);
    setCategoryDraft({
      parent_id: cat.parent_id ? String(cat.parent_id) : "",
      title: cat.title || "",
      sort_order: String(cat.sort_order ?? 0),
    });
  };

  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setCategoryDraft({ parent_id: "", title: "", sort_order: 0 });
  };

  const onMenuItemSubmit = async (event) => {
    event.preventDefault();
    if (!itemDraft.category_id || !itemDraft.title.trim()) return;
    try {
      const payload = {
        category_id: Number(itemDraft.category_id),
        title: itemDraft.title,
        description: itemDraft.description || null,
        price_rub: Number(itemDraft.price_rub || 0),
        sort_order: Math.max(0, parseInt(String(itemDraft.sort_order).trim(), 10) || 0),
      };
      if (editingMenuItemId) {
        await patchAdminMenuItem(editingMenuItemId, payload);
      } else {
        await createAdminMenuItem(payload);
      }
      setItemDraft((prev) => ({
        ...prev,
        title: "",
        description: "",
        price_rub: "",
        sort_order: "0",
      }));
      setEditingMenuItemId(null);
      await loadSection("menu");
    } catch (err) {
      setError(err?.message || "Не удалось добавить лот меню");
    }
  };

  const toggleMenuItem = async (item) => {
    try {
      await patchAdminMenuItem(item.id, { is_active: !item.is_active });
      await loadSection("menu");
    } catch (err) {
      setError(err?.message || "Не удалось изменить статус лота");
    }
  };

  const startEditMenuItem = (item) => {
    setEditingMenuItemId(item.id);
    setItemDraft({
      category_id: String(item.category_id),
      title: item.title || "",
      description: item.description || "",
      price_rub: String(item.price_rub ?? 0),
      sort_order: String(item.sort_order ?? 0),
    });
  };

  const cancelEditMenuItem = () => {
    setEditingMenuItemId(null);
    setItemDraft((prev) => ({
      ...prev,
      title: "",
      description: "",
      price_rub: "",
      sort_order: "0",
    }));
  };

  const removeMenuItem = async (item) => {
    if (!confirmDelete("лот меню", item?.title)) return;
    try {
      await deleteAdminMenuItem(item.id);
      await loadSection("menu");
    } catch (err) {
      setError(err?.message || "Не удалось удалить лот меню");
    }
  };

  return (
    <section className="admin">
      <header className="sectionHeader">
        <h2>Админ-панель</h2>
        <p>Управление контентом.</p>
      </header>

      <div className="eventsToolbar" style={{ marginBottom: 14 }}>
        <Button type="button" variant="accent" onClick={onOpenGame}>
          Мини-игра «Ящерка и дым» (тест)
        </Button>
      </div>

      <div className="adminSwitcher">
        <label className="field">
          Раздел
          <select value={section} onChange={onSectionChange}>
            <option value="events">Мероприятия</option>
            <option value="actions">Акции</option>
            <option value="market">Маркет и мерч</option>
            <option value="menu">Меню</option>
            <option value="guestBase">База гостей</option>
            {canManageHostess ? <option value="hostess">Хостес</option> : null}
          </select>
        </label>
      </div>

      {error ? <p className="errorMessage">{error}</p> : null}

      {section === "events" ? (
        <AdminEventsSection
          eventDraft={eventDraft}
          setEventDraft={setEventDraft}
          uploadingEventImage={uploadingEventImage}
          onEventImageChange={onEventImageChange}
          onEventSubmit={onEventSubmit}
          loading={loading}
          events={events}
          togglePublished={togglePublished}
          removeEvent={removeEvent}
          tournamentGuestSearchByEventId={tournamentGuestSearchByEventId}
          tournamentGuestCandidatesByEventId={tournamentGuestCandidatesByEventId}
          loadingTournamentGuestSearchEventId={loadingTournamentGuestSearchEventId}
          savingTournamentParticipantsEventId={savingTournamentParticipantsEventId}
          onTournamentGuestSearchChange={onTournamentGuestSearchChange}
          addTournamentParticipant={addTournamentParticipant}
          removeTournamentParticipant={removeTournamentParticipant}
          toggleTournamentParticipantsOpen={toggleTournamentParticipantsOpen}
          openedTournamentParticipantsEventId={openedTournamentParticipantsEventId}
          seedTournament={seedTournament}
          pickTournamentWinner={pickTournamentWinner}
          refreshTournament={refreshTournament}
        />
      ) : null}

      {section === "actions" ? (
        <AdminActionsSection
          actionDraft={actionDraft}
          setActionDraft={setActionDraft}
          editingActionId={editingActionId}
          onActionSubmit={onActionSubmit}
          startEditAction={startEditAction}
          cancelEditAction={cancelEditAction}
          loading={loading}
          actions={actions}
          toggleAction={toggleAction}
          removeAction={removeAction}
          actionEntriesById={actionEntriesById}
          loadingActionEntriesId={loadingActionEntriesId}
          loadActionEntries={loadActionEntries}
        />
      ) : null}

      {section === "market" ? (
        <AdminMarketSection
          onSectionSubmit={onSectionSubmit}
          sectionDraft={sectionDraft}
          setSectionDraft={setSectionDraft}
          loading={loading}
          marketSections={marketSections}
          toggleMarketSection={toggleMarketSection}
          removeMarketSection={removeMarketSection}
          onMarketSubmit={onMarketSubmit}
          editingMarketItemId={editingMarketItemId}
          marketDraft={marketDraft}
          setMarketDraft={setMarketDraft}
          onMarketImageChange={onMarketImageChange}
          uploadingImage={uploadingImage}
          onSetMarketPreview={onSetMarketPreview}
          onRemoveMarketImage={onRemoveMarketImage}
          resetMarketDraft={resetMarketDraft}
          marketItems={marketItems}
          marketReservations={marketReservations}
          toggleMarketItem={toggleMarketItem}
          startEditMarketItem={startEditMarketItem}
          removeMarketItem={removeMarketItem}
        />
      ) : null}

      {section === "guestBase" ? (
        <AdminGuestBaseSection
          loading={guestBaseLoading}
          rows={guestBaseRows}
          search={guestBaseSearch}
          setSearch={setGuestBaseSearch}
          onRefresh={() => loadGuestBase(guestBaseSearch)}
          mode="guestBase"
        />
      ) : null}

      {section === "hostess" ? (
        <AdminGuestBaseSection
          loading={hostessLoading}
          rows={hostessRows}
          search={hostessSearch}
          setSearch={setHostessSearch}
          onRefresh={() => loadHostess(hostessSearch)}
          mode="hostess"
          canManageHostess={canManageHostess}
        />
      ) : null}

      {section === "menu" ? (
        <AdminMenuSection
          loading={loading}
          categoryDraft={categoryDraft}
          setCategoryDraft={setCategoryDraft}
          onCategorySubmit={onCategorySubmit}
          editingCategoryId={editingCategoryId}
          startEditCategory={startEditCategory}
          cancelEditCategory={cancelEditCategory}
          menuCategories={menuCategories}
          removeMenuCategory={removeMenuCategory}
          itemDraft={itemDraft}
          setItemDraft={setItemDraft}
          onMenuItemSubmit={onMenuItemSubmit}
          editingMenuItemId={editingMenuItemId}
          startEditMenuItem={startEditMenuItem}
          cancelEditMenuItem={cancelEditMenuItem}
          menuItems={menuItems}
          toggleMenuItem={toggleMenuItem}
          removeMenuItem={removeMenuItem}
        />
      ) : null}
    </section>
  );
}
